/**
 * Phase 2A-ii — Stage B panels
 *   - LightingPropertiesPanel  → per-item canonical lighting UI
 *   - RenderEnvironmentSelector → project-level presentation preset
 *   - RenderReadinessPanel     → resolved readiness for a saved Option
 *
 * The panels never invent business rules. They read backend truth:
 *   • LIGHTING_TYPES membership → whether a lighting panel renders.
 *   • Kelvin / brightness / dimming / beam angle presets are
 *     designer-friendly labels; canonical values remain integers.
 *   • Render Environment is presentation only (backend refuses to
 *     mutate scene_hash for it — verified in Phase 2A-i tests).
 *   • Render readiness statuses (`Ready`, `Warning`, `Blocking`) come
 *     from /api/projects/{pid}/scene-versions/{svid}/render-readiness.
 */
import { useCallback, useEffect, useState } from "react";
import { Lightbulb, Sun, Moon, Building2, CircleDot, Sparkles,
         CircleAlert, CircleCheck, Ban, ExternalLink } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { DraftNumInput } from "@/components/SchematicPanels";

// Frontend mirror of the backend `LIGHTING_TYPES` in
// visual_asset_service.py — the render decides whether to expose the
// beam-angle control per item.
export const LIGHTING_TYPES = {
  downlight:     { label: "Downlight",       kind: "directional", beam: true  },
  wall_light:    { label: "Wall Light",      kind: "wall_wash",   beam: false },
  led_strip:     { label: "LED Strip",       kind: "linear",      beam: false },
  perimeter_led: { label: "Perimeter LED",   kind: "perimeter",   beam: false },
};

const KELVIN_PRESETS  = [2200, 2700, 3000, 3500, 4000];
const BEAM_PRESETS    = [24, 36, 60];

// ── Lighting Properties Panel ──────────────────────────────────────
export function LightingPropertiesPanel({ it, onChange }) {
  const meta = LIGHTING_TYPES[it.type];
  if (!meta) return null;
  const light = it.lighting || {};
  const set = (patch) => onChange({ ...light, ...patch });
  return (
    <div className="pt-3 border-t border-border" data-testid="lighting-panel">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5}/>
        <div className="overline text-[10px]">Lighting — {meta.label}</div>
      </div>
      <label className="inline-flex items-center gap-2 text-[11px] mb-2">
        <input type="checkbox"
               data-testid="lighting-on"
               checked={light.on !== false}
               onChange={(e) => set({ on: e.target.checked })}/>
        <span>On</span>
      </label>
      <div className="mb-2">
        <label className="overline text-[10px] block mb-1">Colour temperature (K)</label>
        <div className="grid gap-1"
             style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}
             data-testid="lighting-kelvin-presets">
          {KELVIN_PRESETS.map((k) => (
            <button key={k} type="button"
                    data-testid={`lighting-kelvin-${k}`}
                    aria-pressed={light.kelvin === k}
                    onClick={() => set({ kelvin: k })}
                    className={`h-8 text-[10px] border ${light.kelvin === k ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
              {k}
            </button>
          ))}
          <DraftNumInput testid="lighting-kelvin-custom"
                         value={KELVIN_PRESETS.includes(light.kelvin) ? "" : (light.kelvin ?? "")}
                         min={1500} max={6500}
                         className="luxe-input h-8 text-[10px] text-center min-w-0"
                         placeholder="Custom"
                         aria-label="Custom Kelvin"
                         onCommit={(v) => set({ kelvin: v })}/>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="overline text-[10px] block mb-1">Brightness (%)</label>
          <DraftNumInput testid="lighting-brightness"
                         value={light.brightness_pct ?? ""}
                         min={0} max={100}
                         onCommit={(v) => set({ brightness_pct: v })}/>
        </div>
        <div>
          <label className="overline text-[10px] block mb-1">Dimming (%)</label>
          <DraftNumInput testid="lighting-dimming"
                         value={light.dimming_pct ?? ""}
                         min={0} max={100}
                         onCommit={(v) => set({ dimming_pct: v })}/>
        </div>
      </div>
      {meta.beam && (
        <div className="mb-2">
          <label className="overline text-[10px] block mb-1">Beam angle (°)</label>
          <div className="grid gap-1"
               style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
               data-testid="lighting-beam-presets">
            {BEAM_PRESETS.map((b) => (
              <button key={b} type="button"
                      data-testid={`lighting-beam-${b}`}
                      aria-pressed={light.beam_angle_deg === b}
                      onClick={() => set({ beam_angle_deg: b })}
                      className={`h-8 text-[10px] border ${light.beam_angle_deg === b ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
                {b}°
              </button>
            ))}
            <DraftNumInput testid="lighting-beam-custom"
                           value={BEAM_PRESETS.includes(light.beam_angle_deg) ? "" : (light.beam_angle_deg ?? "")}
                           min={5} max={120}
                           className="luxe-input h-8 text-[10px] text-center min-w-0"
                           placeholder="Custom"
                           aria-label="Custom Beam Angle"
                           onCommit={(v) => set({ beam_angle_deg: v })}/>
          </div>
        </div>
      )}
      <div>
        <label className="overline text-[10px] block mb-1">Render emphasis</label>
        <select data-testid="lighting-render-emphasis"
                className="luxe-input w-full"
                value={light.render_emphasis || "auto"}
                onChange={(e) => set({ render_emphasis: e.target.value })}>
          <option value="auto">Auto</option>
          <option value="hero">Hero (visible bloom)</option>
          <option value="ambient">Ambient (soft fill)</option>
          <option value="off_camera">Off-camera (contributes light only)</option>
        </select>
      </div>
      <div className="text-[9px] text-muted-foreground mt-2">
        Lighting design is canonical scene data. Change the presentation preset via the
        Render Environment selector — it does not modify these values.
      </div>
    </div>
  );
}

// ── Render Environment Selector (project-level) ────────────────────
const ENV_ICON = {
  natural_daylight:  Sun,
  evening:           Moon,
  warm_architectural:Building2,
  neutral_studio:    CircleDot,
  client_hero:       Sparkles,
};

export function RenderEnvironmentSelector({ projectId, currentKey, onChange }) {
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    api.get("/render-environments").then(({ data }) => {
      setPresets(data.presets || []);
    }).catch((e) => toast.error(formatErr(e)));
  }, []);
  const pick = useCallback(async (key) => {
    try {
      await api.patch(`/projects/${projectId}/render-environment`,
                      { render_environment: key });
      onChange && onChange(key);
      toast.success(`Render environment: ${key.replaceAll("_", " ")}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [projectId, onChange]);
  return (
    <div data-testid="render-environment-selector" className="space-y-2">
      <div className="overline text-[10px]">Render Environment</div>
      <div className="grid gap-1"
           style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
        {presets.map((p) => {
          const Icon = ENV_ICON[p.key] || Sun;
          const active = currentKey === p.key;
          return (
            <button key={p.key} type="button"
                    data-testid={`render-env-${p.key}`}
                    aria-pressed={active}
                    title={p.description}
                    onClick={() => pick(p.key)}
                    className={`p-2 border flex flex-col items-center gap-1 min-h-[60px] ${active ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5}/>
              <span className="text-[9px] text-center leading-tight">{p.label}</span>
            </button>
          );
        })}
      </div>
      <div className="text-[9px] text-muted-foreground">
        Presentation only — never changes fixtures, quantities, geometry or pricing.
      </div>
    </div>
  );
}

// ── Render Readiness Panel ─────────────────────────────────────────
// Reads the backend readiness endpoint for the currently-selected saved
// Design Option. If no Option has been saved yet, the panel invites the
// user to save one first. Warnings and Blocking issues expose
// click-to-navigate remediation to the relevant Material.
const READINESS_STATUS_ICON = {
  ready:    CircleCheck,
  warning:  CircleAlert,
  blocking: Ban,
};

export function RenderReadinessPanel({ projectId, sceneVersionId, onOpenMaterial }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  useEffect(() => {
    if (!projectId || !sceneVersionId) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    api.get(`/projects/${projectId}/scene-versions/${sceneVersionId}/render-readiness`)
      .then(({ data }) => setState({ loading: false, data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: formatErr(e) }));
  }, [projectId, sceneVersionId]);
  if (!sceneVersionId) {
    return (
      <div className="border border-border p-2 text-[11px] text-muted-foreground"
           data-testid="render-readiness-empty">
        Save a Design Option to inspect its Render Readiness.
      </div>
    );
  }
  if (state.loading) return <div className="text-[11px] text-muted-foreground">Loading readiness…</div>;
  if (state.error) return <div className="text-[11px] text-destructive">{state.error}</div>;
  const data = state.data || { status: "ready", issues: [] };
  const Icon = READINESS_STATUS_ICON[data.status] || CircleCheck;
  const label = { ready: "Ready for 3D", warning: "Warnings present", blocking: "Blocking issues" }[data.status] || data.status;
  return (
    <div data-testid="render-readiness-panel" className="border border-border p-2 space-y-2">
      <div className={`flex items-center gap-2 text-[11px] font-medium ${data.status === "blocking" ? "text-destructive" : data.status === "warning" ? "text-amber-600" : "text-emerald-600"}`}
           data-testid="render-readiness-status">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75}/>
        <span>{label}</span>
      </div>
      {(data.issues || []).length === 0 && (
        <div className="text-[10px] text-muted-foreground">
          All pinned visual assets resolve. Lighting snapshot captured.
        </div>
      )}
      {(data.issues || []).map((iss, idx) => {
        const Icn = READINESS_STATUS_ICON[iss.severity] || CircleAlert;
        return (
          <div key={idx}
               data-testid={`readiness-issue-${idx}`}
               data-severity={iss.severity}
               className="flex items-start gap-2 text-[10px] py-1 border-t border-border">
            <Icn className={`w-3 h-3 mt-0.5 shrink-0 ${iss.severity === "blocking" ? "text-destructive" : "text-amber-600"}`} strokeWidth={1.75}/>
            <div className="min-w-0 flex-1">
              <div className="font-medium">{iss.code || iss.severity}</div>
              <div className="text-muted-foreground">{iss.message || ""}</div>
              {iss.material_id && onOpenMaterial && (
                <button type="button"
                        data-testid={`readiness-open-material-${idx}`}
                        onClick={() => onOpenMaterial(iss.material_id)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] underline text-foreground">
                  Open material <ExternalLink className="w-2.5 h-2.5" strokeWidth={1.5}/>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Material Mapping Panel (Stage C physical mapping) ─────────────
// Per-slot physical texture mapping for a scene item. Overrides live
// on `it.material_slots[slotKey].mapping` and NEVER mutate the master
// Material — a project/component override is completely local. The
// Master Material owns its own default mapping (edited in Material
// Admin Visual Assets). Component overrides win at resolve time.
//
// Fields (only rendered where category makes sense — see contextFor):
//   texture_width_mm / texture_height_mm  (physical scale, all)
//   orientation                            (all: normal | rotated_90 | rotated_180 | rotated_270)
//   grain_direction                        (timber: horizontal | vertical)
//   slab_mode                              (stone: repeat | slab)
//   vein_direction                         (stone slab: horizontal | vertical | diagonal)
export const MAPPING_ORIENTATIONS = [
  { v: "normal",       l: "Normal (0°)"   },
  { v: "rotated_90",   l: "Rotated 90°"   },
  { v: "rotated_180",  l: "Rotated 180°"  },
  { v: "rotated_270",  l: "Rotated 270°"  },
];
const GRAIN_OPTIONS = [
  { v: "horizontal", l: "Horizontal" },
  { v: "vertical",   l: "Vertical"   },
];
const SLAB_MODES = [
  { v: "repeat", l: "Repeat / Tile"       },
  { v: "slab",   l: "Slab / Non-Repeating" },
];
const VEIN_OPTIONS = [
  { v: "horizontal", l: "Horizontal" },
  { v: "vertical",   l: "Vertical"   },
  { v: "diagonal",   l: "Diagonal"   },
];

// Category-driven context detection. Timber / stone categories trigger
// grain / slab controls. Every category gets the physical scale row.
function contextFor(materialCategory) {
  const cat = (materialCategory || "").toLowerCase();
  const isTimber = /timber|oak|veneer|walnut|batten|panel/.test(cat);
  const isStone  = /stone|marble|travertine|granite|quartz|slab/.test(cat);
  return { isTimber, isStone };
}

export function MaterialMappingPanel({ slotKey, mapping, materialCategory, onChange }) {
  const m = mapping || {};
  const { isTimber, isStone } = contextFor(materialCategory);
  const patch = (upd) => onChange({ ...m, ...upd });
  const clear = () => onChange(null);
  const isEmpty = !m || (
    !m.texture_width_mm && !m.texture_height_mm &&
    !m.orientation && !m.grain_direction && !m.slab_mode && !m.vein_direction
  );
  return (
    <div className="mt-2 border-l-2 border-border pl-2 space-y-2"
         data-testid={`material-mapping-${slotKey}`}>
      <div className="flex items-center justify-between">
        <div className="overline text-[10px]">Physical mapping</div>
        {!isEmpty && (
          <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid={`material-mapping-${slotKey}-reset`}
                  onClick={clear} title="Reset to master material defaults">
            Reset
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 min-w-0">
        <div className="min-w-0">
          <label className="overline text-[10px] block mb-1">Texture width (mm)</label>
          <DraftNumInput testid={`material-mapping-${slotKey}-width`}
                         value={Number(m.texture_width_mm) || 0}
                         min={0}
                         onCommit={(v) => patch({ texture_width_mm: v || null })}/>
        </div>
        <div className="min-w-0">
          <label className="overline text-[10px] block mb-1">Texture height (mm)</label>
          <DraftNumInput testid={`material-mapping-${slotKey}-height`}
                         value={Number(m.texture_height_mm) || 0}
                         min={0}
                         onCommit={(v) => patch({ texture_height_mm: v || null })}/>
        </div>
      </div>
      <div className="min-w-0">
        <label className="overline text-[10px] block mb-1">Orientation</label>
        <select data-testid={`material-mapping-${slotKey}-orientation`}
                className="luxe-input w-full min-w-0"
                value={m.orientation || "normal"}
                onChange={(e) => patch({ orientation: e.target.value === "normal" ? null : e.target.value })}>
          {MAPPING_ORIENTATIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
      {isTimber && (
        <div className="min-w-0" data-testid={`material-mapping-${slotKey}-timber`}>
          <label className="overline text-[10px] block mb-1">Grain direction</label>
          <select data-testid={`material-mapping-${slotKey}-grain`}
                  className="luxe-input w-full min-w-0"
                  value={m.grain_direction || ""}
                  onChange={(e) => patch({ grain_direction: e.target.value || null })}>
            <option value="">Use master default</option>
            {GRAIN_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
      )}
      {isStone && (
        <div className="space-y-2" data-testid={`material-mapping-${slotKey}-stone`}>
          <div className="min-w-0">
            <label className="overline text-[10px] block mb-1">Slab / repeat mode</label>
            <select data-testid={`material-mapping-${slotKey}-slab-mode`}
                    className="luxe-input w-full min-w-0"
                    value={m.slab_mode || ""}
                    onChange={(e) => patch({ slab_mode: e.target.value || null })}>
              <option value="">Use master default</option>
              {SLAB_MODES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          {m.slab_mode === "slab" && (
            <div className="min-w-0">
              <label className="overline text-[10px] block mb-1">Vein direction</label>
              <select data-testid={`material-mapping-${slotKey}-vein`}
                      className="luxe-input w-full min-w-0"
                      value={m.vein_direction || ""}
                      onChange={(e) => patch({ vein_direction: e.target.value || null })}>
                <option value="">Use master default</option>
                {VEIN_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
